// React hook for fetching translated location names in web app
// Uses GeoNames API with React Query for caching and state management

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

const GEONAMES_BASE_URL = 'https://secure.geonames.org';
const GEONAMES_USERNAME = 'greenie';

// Helper function to fetch translated country name
const getTranslatedCountryName = async (countryCode, lang = 'en') => {
  if (!countryCode) return null;
  
  try {
    const url = `${GEONAMES_BASE_URL}/countryInfoJSON?username=${GEONAMES_USERNAME}&lang=${lang}`;
    const response = await fetch(url);
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.status?.message) {
          errorMessage = `GeoNames API error: ${errorData.status.message}`;
        }
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = `HTTP error! status: ${response.status} ${response.statusText || ''}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Check for GeoNames API errors in response body
    if (data.status && data.status.message) {
      console.warn('GeoNames API returned error:', data.status.message);
      // Return null to indicate failure, caller can use fallback
      return null;
    }
    
    if (data.geonames) {
      const country = data.geonames.find(c => c.countryCode === countryCode);
      return country?.countryName || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching translated country name:', error);
    return null;
  }
};

// Helper function to fetch translated region name
const getTranslatedRegionName = async (regionCode, countryCode, lang = 'en') => {
  if (!regionCode || !countryCode) return null;
  
  try {
    const url = `${GEONAMES_BASE_URL}/searchJSON?country=${countryCode}&featureClass=A&featureCode=ADM1&maxRows=100&lang=${lang}&username=${GEONAMES_USERNAME}`;
    const response = await fetch(url);
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.status?.message) {
          errorMessage = `GeoNames API error: ${errorData.status.message}`;
        }
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = `HTTP error! status: ${response.status} ${response.statusText || ''}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Check for GeoNames API errors in response body
    if (data.status && data.status.message) {
      console.warn('GeoNames API returned error:', data.status.message);
      // Return null to indicate failure, caller can use fallback
      return null;
    }
    
    if (data.geonames) {
      const region = data.geonames.find(r => 
        r.adminCode1 === regionCode || String(r.geonameId) === String(regionCode)
      );
      return region?.name || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching translated region name:', error);
    return null;
  }
};

// Helper function to fetch translated city name
const getTranslatedCityName = async (cityId, countryCode, lang = 'en', regionCode = null) => {
  if (!cityId || !countryCode) return null;
  
  try {
    const url = `${GEONAMES_BASE_URL}/searchJSON?country=${countryCode}&adminCode1=${regionCode || ''}&featureClass=P&lang=${lang}&username=${GEONAMES_USERNAME}&maxRows=1000`;
    const response = await fetch(url);
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.status?.message) {
          errorMessage = `GeoNames API error: ${errorData.status.message}`;
        }
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = `HTTP error! status: ${response.status} ${response.statusText || ''}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Check for GeoNames API errors in response body
    if (data.status && data.status.message) {
      console.warn('GeoNames API returned error:', data.status.message);
      // Return null to indicate failure, caller can use fallback
      return null;
    }
    
    if (data.geonames) {
      const city = data.geonames.find(c => String(c.geonameId) === String(cityId));
      return city?.name || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching translated city name:', error);
    return null;
  }
};

// Main function to get translated location string
const getTranslatedLocationString = async (location, lang = 'en') => {
  if (!location) return '';
  
  const { countryCode, regionCode, cityId, country: fallbackCountry, region: fallbackRegion, city: fallbackCity } = location;
  
  // Format: city, county (region), country
  const parts = [];
  
  // Get city name first
  if (cityId && countryCode) {
    const cityName = await getTranslatedCityName(cityId, countryCode, lang, regionCode);
    if (cityName) {
      parts.push(cityName);
    } else if (fallbackCity) {
      parts.push(fallbackCity);
    }
    // If API fails and no fallback, we'll show nothing for this part
  } else if (fallbackCity) {
    parts.push(fallbackCity);
  }
  
  // Get region (county) name second
  if (regionCode && countryCode) {
    const regionName = await getTranslatedRegionName(regionCode, countryCode, lang);
    if (regionName) {
      parts.push(regionName);
    } else if (fallbackRegion) {
      parts.push(fallbackRegion);
    }
    // If API fails and no fallback, we'll show nothing for this part
  } else if (fallbackRegion) {
    parts.push(fallbackRegion);
  }
  
  // Get country name last
  if (countryCode) {
    const countryName = await getTranslatedCountryName(countryCode, lang);
    if (countryName) {
      parts.push(countryName);
    } else if (fallbackCountry) {
      parts.push(fallbackCountry);
    }
    // If API fails and no fallback, we'll show nothing for this part
  } else if (fallbackCountry) {
    parts.push(fallbackCountry);
  }
  
  return parts.filter(Boolean).join(', ');
};

/**
 * Hook to get translated location string
 * @param {Object} location - Location object with codes and fallback names
 * @returns {Object} { locationString, isLoading, error }
 */
export const useTranslatedLocation = (location) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  
  // Get language directly from i18n instance, with fallback to localStorage
  const getCurrentLang = () => {
    if (i18n?.language) return i18n.language;
    // Fallback: try to get from localStorage
    try {
      const userData = localStorage.getItem('vetUser');
      if (userData) {
        const user = JSON.parse(userData);
        return user?.language || 'en';
      }
    } catch (error) {
      console.error('Error reading language from localStorage:', error);
    }
    return 'en';
  };
  
  const lang = getCurrentLang();
  
  // Invalidate queries when language changes
  useEffect(() => {
    // Invalidate all translated location queries when language changes
    queryClient.invalidateQueries({ 
      queryKey: ['translatedLocation'],
      exact: false 
    });
  }, [i18n?.language, queryClient]);
  
  return useQuery({
    queryKey: ['translatedLocation', location?.countryCode, location?.regionCode, location?.cityId, lang],
    queryFn: () => getTranslatedLocationString(location, lang),
    enabled: !!(location?.countryCode || location?.regionCode || location?.cityId),
    staleTime: 0, // Always refetch when language changes (lang is in queryKey)
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days (formerly cacheTime)
    retry: 1, // Only retry once on failure
  });
};

